import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { Header, OrderStatusBadge } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import constant, { statuses } from "../../constants";
import { useToast, Spinner } from "../../components/ui";
import {
  FiSearch,
  FiRefreshCw,
  FiTruck,
  FiPackage,
  FiCalendar,
  FiFilter,
  FiCopy,
  FiCheck,
} from "react-icons/fi";
import { IoReceiptOutline } from "react-icons/io5";
import { BiLogIn } from "react-icons/bi";
import { HiOutlineDocumentSearch } from "react-icons/hi";

const ShippingStatusBadge = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  if (s === "shipped") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
        จัดส่งแล้ว
      </span>
    );
  }
  if (s === "packing") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
        กำลังเตรียมจัดส่ง
      </span>
    );
  }
  if (s === "canceled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
        ยกเลิกการจัดส่ง
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
      รอดำเนินการ
    </span>
  );
};

const getSlipUrl = (slips) => {
  if (!slips) return null;
  if (Array.isArray(slips)) {
    return slips.length > 0 ? slips[slips.length - 1] : null;
  }
  if (typeof slips === "string") return slips;
  return null;
};

export const OrdersMail = () => {
  const { callApi, configs } = Context();
  const { toast } = useToast();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");

  // Filters
  const [status, setStatus] = useState("");
  const [shippingStatus, setShippingStatus] = useState("");
  const [searchType, setSearchType] = useState("shortId");
  const [searchValue, setSearchValue] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs?.roundDate, roundDate]);

  useEffect(() => {
    if (roundDate) {
      getOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundDate, status, shippingStatus]);

  const getOrders = async () => {
    if (!roundDate) return;
    setLoading(true);
    setCurrentPage(1);

    const queries = [["deliverChannel", "mail"]];

    if (status) {
      queries.push(["status", status]);
    }
    if (shippingStatus) {
      queries.push(["shippingStatus", shippingStatus]);
    }
    if (searchValue.trim()) {
      queries.push([searchType, searchValue.trim()]);
    }

    try {
      const result = await callApi(
        "orders-getAll",
        {
          roundDate,
          queries,
          limit: 500,
          orderBy: ["createdAt", "desc"],
        },
        { notShowLoading: true },
      );

      const ordersList = Array.isArray(result) ? result : [];
      setOrders(ordersList);
    } catch (err) {
      console.error("ordersMail getOrders error:", err);
      toast({
        variant: "destructive",
        title: "ดึงข้อมูลไม่สำเร็จ",
        description: err.message || "เกิดข้อผิดพลาดในการโหลดคำสั่งซื้อไปรษณีย์",
      });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchValue("");
    setStatus("");
    setShippingStatus("");
    setSearchType("shortId");
    setCurrentPage(1);
    // Reload with cleared filters
    if (roundDate) {
      setLoading(true);
      callApi(
        "orders-getAll",
        {
          roundDate,
          queries: [["deliverChannel", "mail"]],
          limit: 500,
          orderBy: ["createdAt", "desc"],
        },
        { notShowLoading: true },
      )
        .then((result) => {
          setOrders(Array.isArray(result) ? result : []);
        })
        .finally(() => setLoading(false));
    }
  };

  // KPIs
  const stats = useMemo(() => {
    let totalTickets = 0;
    let shippedCount = 0;
    let pendingShippingCount = 0;

    orders.forEach((o) => {
      totalTickets += Number(o.size) || 0;
      const ss = String(o.shippingStatus || "pending").toLowerCase();
      if (ss === "shipped") shippedCount++;
      else if (ss === "pending" || ss === "packing") pendingShippingCount++;
    });

    return {
      totalOrders: orders.length,
      totalTickets,
      shippedCount,
      pendingShippingCount,
    };
  }, [orders]);

  // Pagination calculation
  const totalItems = orders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return orders.slice(startIndex, startIndex + pageSize);
  }, [orders, currentPage, pageSize]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6 max-w-[1600px] mx-auto w-full min-w-0 pb-12">
      {/* Header */}
      <Header
        header="Orders"
        subHeader="คำสั่งซื้อสลากทางไปรษณีย์ (จัดส่งพัสดุ)"
        icon={<IoReceiptOutline className="text-xl" />}
      />

      {/* Filter and Control Card */}
      <div className="bg-white rounded-md shadow-xs border border-slate-200/80 p-4 sm:p-5 flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
            <FiFilter className="text-rose-600 text-base" />
            <span>ตัวกรองและเงื่อนไขการค้นหา</span>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            getOrders();
          }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end"
        >
          {/* งวดสลาก */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiCalendar className="text-slate-400" />
              <span>งวดสลาก</span>
            </label>
            <select
              className="h-10 w-full px-3 bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-800 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all cursor-pointer"
              onChange={(e) => setRoundDate(e.target.value)}
              value={roundDate || ""}
            >
              {configs?.roundDates?.map((date, index) => (
                <option key={index} value={date}>
                  งวด {date}
                </option>
              ))}
            </select>
          </div>

          {/* สถานะคำสั่งซื้อ */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600">
              สถานะคำสั่งซื้อ
            </label>
            <select
              className="h-10 w-full px-3 bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-800 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all cursor-pointer"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">ทั้งหมด (All Status)</option>
              <option value={statuses.APPROVE}>ยืนยันแล้ว (Approved)</option>
              <option value={statuses.PAID}>รออนุมัติ (Paid)</option>
              <option value={statuses.PENDING}>รอชำระเงิน (Pending)</option>
              <option value={statuses.CANCEL}>ยกเลิก (Canceled)</option>
            </select>
          </div>

          {/* สถานะการจัดส่ง */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiTruck className="text-slate-400" />
              <span>สถานะพัสดุ</span>
            </label>
            <select
              className="h-10 w-full px-3 bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-800 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all cursor-pointer"
              value={shippingStatus}
              onChange={(e) => setShippingStatus(e.target.value)}
            >
              <option value="">ทั้งหมด (All Shipping)</option>
              <option value="pending">รอดำเนินการ</option>
              <option value="packing">กำลังเตรียมจัดส่ง</option>
              <option value="shipped">จัดส่งแล้ว</option>
              <option value="canceled">ยกเลิกจัดส่ง</option>
            </select>
          </div>

          {/* ค้นหาตาม */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600">
              ค้นหาตาม
            </label>
            <select
              className="h-10 w-full px-3 bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-800 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all cursor-pointer"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="shortId">รหัสคำสั่งซื้อ</option>
              <option value="shippingTrack">เลขพัสดุ (Tracking)</option>
              <option value="phone">เบอร์โทรศัพท์</option>
              <option value="userId">รหัสผู้ใช้</option>
              <option value="firstName">ชื่อ</option>
              <option value="lastName">นามสกุล</option>
            </select>
          </div>

          {/* คำค้นหา */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiSearch className="text-slate-400" />
              <span>คำค้นหา</span>
            </label>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="พิมพ์เพื่อค้นหา..."
              className="h-10 w-full px-3 bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-800 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all placeholder:text-slate-400"
            />
          </div>

          {/* ปุ่มค้นหา & รีเซ็ต */}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 flex-1 flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:opacity-50 cursor-pointer"
            >
              <FiSearch className="w-3.5 h-3.5" />
              <span>ค้นหา</span>
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleReset}
              className="h-10 px-3 flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 text-xs font-semibold rounded-md border border-slate-200 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              title="รีเซ็ตตัวกรองทั้งหมด"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>รีเซ็ต</span>
            </button>
          </div>
        </form>
      </div>

      {/* KPI Cards */}
      {orders.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-blue-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>ออเดอร์ไปรษณีย์ทั้งหมด</span>
              <FiPackage className="text-blue-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-slate-900">
                {stats.totalOrders.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">รายการ</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">ในงวด {roundDate}</span>
          </div>

          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-rose-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>จำนวนสลากที่จัดส่ง</span>
              <IoReceiptOutline className="text-rose-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-rose-600">
                {stats.totalTickets.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">ใบ</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              เฉลี่ย ~{stats.totalOrders ? (stats.totalTickets / stats.totalOrders).toFixed(1) : 0} ใบ/ออเดอร์
            </span>
          </div>

          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-amber-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>รอจัดส่ง / กำลังแพ็ค</span>
              <FiTruck className="text-amber-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-amber-600">
                {stats.pendingShippingCount.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">รายการ</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">สถานะรอดำเนินการหรือเตรียมของ</span>
          </div>

          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-emerald-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>จัดส่งพัสดุแล้ว</span>
              <FiTruck className="text-emerald-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-emerald-600">
                {stats.shippedCount.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">รายการ</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              มีเลขพัสดุและจัดส่งแล้ว
            </span>
          </div>
        </div>
      )}

      {/* Main Table Section */}
      <div className="bg-white rounded-md shadow-xs border border-slate-200/80 overflow-hidden flex flex-col">
        {/* Table Top Bar */}
        <div className="p-3.5 sm:p-4 bg-slate-50/80 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800">
              รายการคำสั่งซื้อไปรษณีย์
            </span>
            <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
              {orders.length.toLocaleString()} รายการ
            </span>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>แสดงต่อหน้า:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="text-xs font-semibold text-slate-800 bg-white border border-slate-200 rounded-md py-1 px-2 focus:outline-none"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>

        {/* Table View */}
        <div className="w-full overflow-x-auto">
          <Table className="w-full text-xs">
            <TableHeader className="bg-slate-100/90 text-slate-700">
              <TableRow>
                <TableHead className="w-12 text-center font-bold">#</TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  เวลาสร้าง (หมดอายุ)
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  รหัสคำสั่งซื้อ
                </TableHead>
                <TableHead className="whitespace-nowrap font-bold">
                  ข้อมูลลูกค้า
                </TableHead>
                <TableHead className="whitespace-nowrap font-bold">
                  ที่อยู่จัดส่งไปรษณีย์
                </TableHead>
                <TableHead className="text-right whitespace-nowrap font-bold">
                  จำนวน
                </TableHead>
                <TableHead className="text-right whitespace-nowrap font-bold">
                  ยอดเงิน
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  ชำระเงิน
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  สลิป
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  สถานะคำสั่งซื้อ
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  สถานะจัดส่ง / เลขพัสดุ
                </TableHead>
                <TableHead className="text-center whitespace-nowrap font-bold">
                  จัดการ
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-[200px]">
                  <TableCell colSpan="12" className="text-center">
                    <div className="flex flex-col items-center justify-center p-12 gap-3 text-slate-400">
                      <Spinner className="w-8 h-8 border-rose-600 text-rose-600" />
                      <p className="text-xs font-semibold text-slate-500">
                        กำลังดึงข้อมูลคำสั่งซื้อไปรษณีย์...
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow className="h-[200px]">
                  <TableCell
                    colSpan="12"
                    className="h-64 font-semibold text-center text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FiPackage className="text-3xl text-slate-300" />
                      <p className="text-xs text-slate-500">ไม่พบคำสั่งซื้อสลากทางไปรษณีย์ในงวดนี้</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order, index) => {
                  const itemIndex = (currentPage - 1) * pageSize + index + 1;
                  return (
                    <OrderMailRow
                      key={order.id || index}
                      order={order}
                      index={itemIndex}
                      getOrders={getOrders}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Footer */}
        {orders.length > 0 && (
          <div className="p-3 sm:p-4 bg-slate-50/80 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <div>
              <span>แสดงแถวที่ </span>
              <span className="font-semibold text-slate-900">
                {((currentPage - 1) * pageSize + 1).toLocaleString()}
              </span>
              <span> ถึง </span>
              <span className="font-semibold text-slate-900">
                {Math.min(currentPage * pageSize, totalItems).toLocaleString()}
              </span>
              <span> จากทั้งหมด </span>
              <span className="font-semibold text-slate-900">
                {totalItems.toLocaleString()}
              </span>
              <span> รายการ</span>
            </div>

            <Pagination className="justify-end w-auto mx-0">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${
                      currentPage > 1
                        ? "cursor-pointer hover:bg-slate-100"
                        : "cursor-not-allowed opacity-40 hover:bg-transparent"
                    } font-semibold`}
                    onClick={() =>
                      currentPage > 1 && setCurrentPage((p) => p - 1)
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((page, idx) => (
                  <PaginationItem key={idx}>
                    {page === "..." ? (
                      <span className="px-2 py-1 text-slate-400">...</span>
                    ) : (
                      <PaginationLink
                        className={`${
                          currentPage === page
                            ? "bg-rose-600 text-white hover:bg-rose-700 font-bold"
                            : "hover:bg-slate-100 font-semibold"
                        } rounded-md cursor-pointer`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    className={`${
                      currentPage < totalPages
                        ? "cursor-pointer hover:bg-slate-100"
                        : "cursor-not-allowed opacity-40 hover:bg-transparent"
                    } font-semibold`}
                    onClick={() =>
                      currentPage < totalPages && setCurrentPage((p) => p + 1)
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

const OrderMailRow = ({ order, index }) => {
  const { callApi } = Context();
  const [tokenLoading, setTokenLoading] = useState(false);
  const [copiedTrack, setCopiedTrack] = useState(false);

  const slipUrl = getSlipUrl(order.slips);
  const fullAddress = [
    order.address?.addr,
    order.address?.subDistrict,
    order.address?.district,
    order.address?.province,
    order.address?.zipcode,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || "-";

  const handleCopyTrack = (track) => {
    if (!track) return;
    navigator.clipboard.writeText(track.trim());
    setCopiedTrack(true);
    setTimeout(() => setCopiedTrack(false), 1500);
  };

  const handleOpenTrackUrl = (track) => {
    if (!track) return;
    window.open(
      `https://track.thailandpost.co.th/?trackNumber=${encodeURIComponent(track.trim())}`,
      "_blank",
    );
  };

  const handleCustomerLogin = async () => {
    setTokenLoading(true);
    try {
      const result = await callApi("users-addAdminToken", {
        userId: order.userId,
      });
      if (result?.adminToken) {
        window.open(constant.getAdminOrderUrl(result.adminToken), "_blank");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTokenLoading(false);
    }
  };

  return (
    <TableRow className="hover:bg-slate-50/80 transition-colors border-b border-slate-100">
      {/* Index */}
      <TableCell className="text-center font-mono text-slate-500 font-semibold">
        {index}
      </TableCell>

      {/* Created & Expire Time */}
      <TableCell className="whitespace-nowrap">
        <div className="flex flex-col text-[11px] font-semibold">
          <span className="text-slate-800">
            {order.createdAt
              ? new Date(order.createdAt).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "-"}
          </span>
          {order.expireAt && order.status === statuses.PENDING && (
            <span className="text-rose-500 text-[10px]">
              (หมดอายุ:{" "}
              {new Date(order.expireAt).toLocaleTimeString("th-TH", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              )
            </span>
          )}
        </div>
      </TableCell>

      {/* Short ID */}
      <TableCell className="text-center font-mono font-bold text-slate-800">
        #{order.shortId || order.id?.slice(-5).toUpperCase() || "-"}
      </TableCell>

      {/* Customer */}
      <TableCell>
        <div className="flex flex-col text-[11px]">
          <span className="font-bold text-slate-900">
            {`${order.firstName || ""} ${order.lastName || ""}`.trim() || "-"}
          </span>
          <span className="font-mono text-slate-500">{order.phone || "-"}</span>
          <span className="font-mono text-[10px] text-slate-400 truncate max-w-[140px]" title={order.userId}>
            ID: {order.userId || "-"}
          </span>
        </div>
      </TableCell>

      {/* Shipping Address */}
      <TableCell className="max-w-[260px]">
        <div className="flex flex-col gap-0.5 text-[11px]">
          <span className="text-slate-700 leading-snug line-clamp-2" title={fullAddress}>
            {fullAddress}
          </span>
          {order.address?.zipcode && (
            <span className="text-[10px] font-mono text-slate-400">
              รหัส ปณ. {order.address.zipcode}
            </span>
          )}
        </div>
      </TableCell>

      {/* Ticket Size */}
      <TableCell className="text-right">
        <span className="font-mono font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200/60">
          {Number(order.size || 0).toLocaleString()}
        </span>
      </TableCell>

      {/* Total Price */}
      <TableCell className="text-right font-mono font-bold text-slate-800">
        {order.paidCurrency === "LAK"
          ? `${order.totalPrice_lak?.toLocaleString()} กีบ`
          : `${Number(order.totalPrice || 0).toLocaleString()} ฿`}
      </TableCell>

      {/* Payment Channel */}
      <TableCell className="text-center">
        {order.paymentChannel === "bank" ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
            โอนเงิน
          </span>
        ) : order.paymentChannel === "qr" ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            QR Code
          </span>
        ) : (
          <span className="text-slate-400">{order.paymentChannel || "-"}</span>
        )}
      </TableCell>

      {/* Slip */}
      <TableCell className="text-center">
        {slipUrl ? (
          <button
            type="button"
            onClick={() => window.open(slipUrl, "_blank")}
            className="group relative inline-block border border-slate-200 rounded overflow-hidden hover:opacity-80 transition-opacity"
            title="คลิกเพื่อดูรูปสลิปขนาดเต็ม"
          >
            <img
              src={slipUrl}
              alt="Slip"
              className="w-10 h-10 object-cover"
            />
          </button>
        ) : (
          <span className="text-slate-300">-</span>
        )}
      </TableCell>

      {/* Order Status */}
      <TableCell className="text-center">
        <OrderStatusBadge status={order.status} size="sm" />
      </TableCell>

      {/* Shipping Status & Tracking */}
      <TableCell className="text-center">
        <div className="flex flex-col items-center gap-1">
          <ShippingStatusBadge status={order.shippingStatus} />

          {order.shippingTrack ? (
            <div className="flex items-center gap-1 mt-0.5">
              <span
                onClick={() => handleOpenTrackUrl(order.shippingTrack)}
                className="font-mono text-[10px] font-bold text-blue-600 hover:underline cursor-pointer"
                title="คลิกเพื่อเช็คพัสดุบนเว็บไปรษณีย์ไทย"
              >
                {order.shippingTrack}
              </span>
              <button
                type="button"
                onClick={() => handleCopyTrack(order.shippingTrack)}
                className="text-slate-400 hover:text-slate-700 text-xs"
                title="คัดลอกเลขพัสดุ"
              >
                {copiedTrack ? (
                  <FiCheck className="text-emerald-500 text-[10px]" />
                ) : (
                  <FiCopy className="text-[10px]" />
                )}
              </button>
            </div>
          ) : (
            <span className="text-[10px] text-slate-400 font-mono">-</span>
          )}
        </div>
      </TableCell>

      {/* Actions */}
      <TableCell className="text-center whitespace-nowrap">
        <div className="flex items-center justify-center gap-1.5">
          {/* Customer View */}
          {order.userId && (
            <button
              type="button"
              disabled={tokenLoading}
              onClick={handleCustomerLogin}
              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-200 active:scale-95 transition-all disabled:opacity-50"
              title="เปิดดูมุมมองลูกค้า (Admin Token)"
            >
              {tokenLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <BiLogIn className="text-sm" />
              )}
            </button>
          )}

          {/* Open Order Detail */}
          <button
            type="button"
            onClick={() => window.open(`/orders/${order.id}`, "_blank")}
            className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-md border border-rose-200 active:scale-95 transition-all"
            title="ดูรายละเอียดคำสั่งซื้อ"
          >
            <HiOutlineDocumentSearch className="text-sm" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default OrdersMail;
